import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { CheckIcon } from 'lucide-react'
import { cn } from '../../../lib/utils'

/**
 * Chat message bubble.
 * - `variant="incoming"` — grey bubble on the left (messages from user/tenant)
 * - `variant="outgoing"` — brand-light bubble on the right (our messages)
 * - `variant="error"` — destructive-light bubble with "Not sent" footer
 */
const chatMessageVariants = cva(
	'relative flex w-fit max-w-[85%] flex-col gap-1 rounded-(--radius-md) px-3 py-2 text-sm leading-5',
	{
		variants: {
			variant: {
				incoming: 'self-start bg-(--color-muted) text-(--color-foreground)',
				outgoing: 'self-end bg-(--color-brand-light) text-(--color-foreground)',
				error:    'self-end bg-(--color-destructive-light) text-(--color-foreground)',
			},
		},
		defaultVariants: { variant: 'incoming' },
	}
)

export interface ChatMessageProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof chatMessageVariants> {
	time?: string
	/** Shown only on outgoing messages: single tick = sent, double = read */
	readStatus?: 'sent' | 'read' | null
	/** Shown below the content for `variant="error"` */
	errorText?: string
}

const ChatMessage = React.forwardRef<HTMLDivElement, ChatMessageProps>(
	({ className, variant, time, readStatus, errorText, children, ...props }, ref) => (
		<div
			ref={ref}
			data-slot="chat-message"
			className={cn(chatMessageVariants({ variant }), className)}
			{...props}
		>
			<div className="whitespace-pre-wrap break-words">{children}</div>
			<div className="flex items-center justify-end gap-1 text-xs leading-4 text-(--color-muted-foreground)">
				{errorText && variant === 'error' ? (
					<span className="text-(--color-destructive)">{errorText}</span>
				) : (
					time && <span>{time}</span>
				)}
				{readStatus && variant === 'outgoing' && (
					<span className="flex items-center">
						<CheckIcon className="size-3" />
						{readStatus === 'read' && <CheckIcon className="-ml-1.5 size-3" />}
					</span>
				)}
			</div>
		</div>
	)
)

ChatMessage.displayName = 'ChatMessage'
export { ChatMessage, chatMessageVariants }
